import { useEffect, useState, useRef } from 'react';
import { compatApi } from '../../utils/Intercepter';
import useTokenStore from '../../stores/tokens';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import OrgSectionTabs from '../../components/OrgSectionTabs';

// 직급/부서처럼 "조직별 이름 목록 + 서열"을 드래그로 편집하는 공용 에디터.
// 저장은 항상 전체 교체 방식 — 서버(OrgPositionService/OrgDepartmentService)와 대칭.
function ReorderableListEditor({ heading, hint, itemLabel, itemNamePlural, fetchPath, savePath, saveField, itemField, isMaster }) {
    const { user } = useTokenStore();
    const [items, setItems] = useState([]); // string[]
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const dragIndex = useRef(null);
    const [dragOver, setDragOver] = useState(null);

    useEffect(() => {
        const orgCode = user?.currentOrganizationCode;
        if (!orgCode) { setLoading(false); return; }
        setLoading(true);
        (async () => {
            try {
                const res = await compatApi.get(`${fetchPath}/${orgCode}`);
                if (res.ok) setItems((res.data || []).map(p => p[itemField]));
            } catch (e) {
                // 조회 실패는 조용히(빈 목록). 권한 문제는 인터셉터가 처리.
            } finally {
                setLoading(false);
            }
        })();
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const reorder = (from, to) => {
        if (from == null || to == null || from === to) return;
        const next = [...items];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setItems(next);
    };
    const rename = (idx, value) => {
        const next = [...items];
        next[idx] = value;
        setItems(next);
    };
    const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
    const add = () => setItems([...items, '']);

    const handleSave = async () => {
        const cleaned = items.map(p => p.trim()).filter(Boolean);
        if (cleaned.length !== new Set(cleaned).size) {
            toast.warn(`중복된 ${itemLabel}명이 있습니다.`, toastConfig);
            return;
        }
        setSaving(true);
        try {
            const res = await compatApi.post(savePath, {
                organizationCode: user.currentOrganizationCode,
                [saveField]: cleaned,
            });
            if (res.ok) {
                toast.success(`${itemLabel}이(가) 저장되었습니다.`, toastConfig);
                setItems(cleaned);
            } else {
                toast.error(`${itemLabel} 저장에 실패했습니다.`, toastConfig);
            }
        } catch (error) {
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : `${itemLabel} 저장 중 오류가 발생했습니다.`, toastConfig);
        } finally {
            setSaving(false);
        }
    };

    const iconBtn = (disabled) => ({
        width: 30, height: 30, borderRadius: 8, border: '1px solid #ececf2',
        background: 'white', color: disabled ? '#c7c5d2' : '#5d5b6e',
        cursor: disabled ? 'default' : 'pointer', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    });

    return (
        <div style={{
            background: 'white', borderRadius: 18, border: '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
            overflow: 'hidden',
        }}>
            <div style={{
                padding: '16px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                borderBottom: '1px solid #ececf2',
            }}>
                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{heading}</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#8b8a99' }}>{hint}</p>
            </div>

            <div style={{ padding: '18px 22px' }}>
                {loading ? (
                    <div style={{ padding: '28px 0', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>불러오는 중…</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {items.length === 0 && (
                                <div style={{ padding: '20px 12px', textAlign: 'center', color: '#8b8a99', fontSize: 13 }}>
                                    등록된 {itemNamePlural}이(가) 없습니다. {isMaster && `아래 "${itemLabel} 추가"로 시작하세요.`}
                                </div>
                            )}
                            {items.map((name, idx) => (
                                <div key={idx}
                                    draggable={isMaster}
                                    onDragStart={() => { dragIndex.current = idx; }}
                                    onDragOver={(e) => { if (!isMaster) return; e.preventDefault(); if (dragOver !== idx) setDragOver(idx); }}
                                    onDrop={(e) => { e.preventDefault(); reorder(dragIndex.current, idx); dragIndex.current = null; setDragOver(null); }}
                                    onDragEnd={() => { dragIndex.current = null; setDragOver(null); }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: 3, borderRadius: 10,
                                        background: dragOver === idx ? hexToRgba(PURPLE, .07) : 'transparent',
                                        outline: dragOver === idx ? `1.5px dashed ${hexToRgba(PURPLE, .45)}` : '1.5px solid transparent',
                                        transition: 'background 100ms',
                                    }}>
                                    {isMaster && (
                                        <span title="드래그해서 순서 변경" style={{ cursor: 'grab', color: '#c7c5d2', display: 'inline-flex', flexShrink: 0, padding: '0 2px' }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>
                                        </span>
                                    )}
                                    <span style={{ width: 22, fontSize: 12, color: '#aaa9b7', textAlign: 'right', flexShrink: 0 }}>{idx + 1}</span>
                                    <input
                                        type="text" value={name} disabled={!isMaster}
                                        onChange={(e) => rename(idx, e.target.value)}
                                        placeholder={`${itemLabel}명`}
                                        style={{
                                            flex: 1, height: 38, borderRadius: 9, border: '1.5px solid #ececf2',
                                            background: isMaster ? '#fafaff' : '#f5f4fa', padding: '0 12px',
                                            fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
                                        }}
                                    />
                                    {isMaster && (
                                        <button style={{ ...iconBtn(false), color: '#dc2626', borderColor: '#fecaca' }} onClick={() => remove(idx)} title="삭제">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {isMaster && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 8 }}>
                                <button onClick={add} style={{
                                    height: 36, padding: '0 14px', borderRadius: 9, border: `1.5px dashed ${hexToRgba(PURPLE, .35)}`,
                                    background: 'white', color: PURPLE, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                }}>+ {itemLabel} 추가</button>
                                <button onClick={handleSave} disabled={saving} style={{
                                    height: 36, padding: '0 20px', borderRadius: 9, border: 'none',
                                    background: saving ? '#c7c5d2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                    color: 'white', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
                                }}>{saving ? '저장 중…' : '저장'}</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// 조직 구성원 중 일부를 골라 이메일 목록으로 확정하는 공용 선택 모달.
// 부 관리자 등록, 트리거별 수신자 설정 등 "구성원 여러 명 고르기"가 필요한 곳에서 공유한다.
// 선택 확정(onConfirm)만 하고 실제 저장/새로고침은 호출부 책임 — 여기선 API를 모른다.
function MemberPickerModal({ orgCode, title, initialSelected, saving, onCancel, onConfirm }) {
    const { user } = useTokenStore();
    const [searchKeyword, setSearchKeyword] = useState('');
    const [allUserList, setAllUserList] = useState([]);
    const [selected, setSelected] = useState(initialSelected);
    const [loadingUsers, setLoadingUsers] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingUsers(true);
            try {
                const res = await compatApi.post('/project/findWorkmanList', { user: user || '', organizationCode: orgCode });
                if (!res.ok) throw new Error();
                const userList = res.data || [];
                // 앱 설치(푸시 수신 가능) 여부 뱃지 — 실패해도 목록 자체는 보여준다
                if (userList.length > 0) {
                    try {
                        // organizationCode 없이 보내면 서버 인가(@orgAccessPolicy.isOrgMember)가 조직을 특정 못 해 403이 난다
                        const deviceRes = await compatApi.post(
                            `/push/device/find?organizationCode=${encodeURIComponent(orgCode)}`,
                            userList.map(p => p.userEmail));
                        if (deviceRes.ok) {
                            const appUserEmails = deviceRes.data || [];
                            if (!cancelled) setAllUserList(userList.map(p => ({ ...p, isAppUser: appUserEmails.includes(p.userEmail) })));
                            return;
                        }
                    } catch (e) { /* 뱃지 없이 진행 */ }
                }
                if (!cancelled) setAllUserList(userList);
            } catch (e) {
                toast.error('구성원 목록을 불러오는데 실패했습니다.', toastConfig);
            } finally {
                if (!cancelled) setLoadingUsers(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orgCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (person) => {
        setSelected(list => list.some(p => p.userEmail === person.userEmail)
            ? list.filter(p => p.userEmail !== person.userEmail)
            : [...list, person]);
    };

    const filteredUsers = () => {
        const keyword = searchKeyword.trim().toLowerCase();
        const list = allUserList.filter(p => p.userEmail !== user?.userEmail); // 본인 제외
        if (!keyword) return list;
        return list.filter(p =>
            (p.userName && p.userName.toLowerCase().includes(keyword)) ||
            (p.userEmail && p.userEmail.toLowerCase().includes(keyword)));
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 540, boxShadow: '0 8px 32px rgba(60,50,120,.2)', display: 'flex', flexDirection: 'column', maxHeight: '85vh', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #ececf2' }}>
                    <h5 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: '#1f1d2e' }}>{title}</h5>
                    <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b8a99', padding: 4 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    </button>
                </div>

                <div style={{ padding: '14px 24px', borderBottom: '1px solid #f4f3f9', background: '#fafaff' }}>
                    <input type="text" placeholder="이름이나 이메일로 검색" value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        style={{
                            width: '100%', height: 38, borderRadius: 10, border: '1.5px solid #ececf2',
                            background: 'white', padding: '0 12px', fontSize: 13.5, color: '#1f1d2e',
                            outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                        }} />
                </div>

                <div style={{ flex: 1, overflowY: 'auto', minHeight: 280 }}>
                    {loadingUsers ? (
                        <div style={{ padding: '60px 0', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>불러오는 중…</div>
                    ) : filteredUsers().length > 0 ? (
                        filteredUsers().map((person) => {
                            const isSelected = selected.some(p => p.userEmail === person.userEmail);
                            return (
                                <div key={person.userEmail} onClick={() => toggle(person)} style={{
                                    display: 'flex', alignItems: 'center', padding: '11px 24px', cursor: 'pointer',
                                    borderBottom: '1px solid #f4f3f9',
                                    background: isSelected ? hexToRgba(PURPLE, 0.04) : 'white',
                                }}>
                                    <input type="checkbox" checked={isSelected} readOnly
                                        style={{ accentColor: PURPLE, width: 16, height: 16, marginRight: 14, cursor: 'pointer' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1f1d2e' }}>{person.userName}</div>
                                        <div style={{ fontSize: 12, color: '#8b8a99', marginTop: 1 }}>{person.userEmail}</div>
                                    </div>
                                    {person.isAppUser && (
                                        <span style={{ fontSize: 10.5, fontWeight: 700, color: MINT, background: hexToRgba(MINT, 0.12), padding: '2px 8px', borderRadius: 6 }}>App</span>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ textAlign: 'center', color: '#aaa9b7', padding: '56px 0', fontSize: 13.5 }}>
                            {searchKeyword ? '검색 결과가 없습니다.' : '조직 구성원이 없습니다.'}
                        </div>
                    )}
                </div>

                <div style={{ padding: '14px 24px', borderTop: '1px solid #ececf2', background: '#fafaff', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button onClick={onCancel} style={{
                        height: 36, padding: '0 16px', borderRadius: 9, border: '1px solid #d1d5db',
                        background: 'white', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>취소</button>
                    <button onClick={() => onConfirm(selected)} disabled={saving} style={{
                        height: 36, padding: '0 20px', borderRadius: 9, border: 'none',
                        background: saving ? '#c7c5d2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                        color: 'white', fontSize: 13, fontWeight: 600,
                        cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
                    }}>{saving ? '저장 중…' : '선택 완료'}</button>
                </div>
            </div>
        </div>
    );
}

// 조직 알림 트리거(연봉협상일 N일 전/당일 등) 설정 — MASTER 전용.
// 트리거는 상위 그룹(예: "연봉협상일 알림") 아래 여러 개가 묶여 있고, 그룹 스위치는
// 화면 편의 토글이 아니라 실제 게이트다 — 꺼지면 하위 트리거가 개별적으로 켜져 있어도
// 서버 배치가 발송하지 않는다(MasterPushTriggerService 참고). 그래서 그룹 OFF일 땐
// 하위 항목을 접어서, 켜야만 의미가 생기는 하위 설정을 보여주지 않는다.
// 그룹/트리거 카탈로그는 서버 enum이 단일 출처라 이 화면은 GET 결과를 그대로 렌더링한다.
function MasterTriggerSettings() {
    const { user } = useTokenStore();
    const orgCode = user?.currentOrganizationCode;
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingGroupCode, setSavingGroupCode] = useState(null);
    const [savingCode, setSavingCode] = useState(null);
    const [recipientModalFor, setRecipientModalFor] = useState(null); // 수신자 편집 중인 triggerCode | null
    const [savingRecipients, setSavingRecipients] = useState(false);

    const fetchTriggers = async () => {
        if (!orgCode) { setLoading(false); return; }
        try {
            const res = await compatApi.get(`/push/master-trigger/${orgCode}`);
            if (res.ok) setGroups(res.data || []);
        } catch (e) {
            // 조회 실패는 빈 목록. 권한 문제는 인터셉터가 처리.
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { setLoading(true); fetchTriggers(); }, [orgCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchGroup = (groupCode, fields) =>
        setGroups(gs => gs.map(g => (g.groupCode === groupCode ? { ...g, ...fields } : g)));
    const patchTrigger = (triggerCode, fields) =>
        setGroups(gs => gs.map(g => ({
            ...g,
            triggers: g.triggers.map(t => (t.triggerCode === triggerCode ? { ...t, ...fields } : t)),
        })));
    const findTrigger = (triggerCode) => {
        for (const g of groups) {
            const t = g.triggers.find(x => x.triggerCode === triggerCode);
            if (t) return t;
        }
        return null;
    };

    // 그룹 게이트는 토글 즉시 저장(낙관적 반영, 실패 시 롤백)
    const toggleGroup = async (g) => {
        const next = !g.enabled;
        patchGroup(g.groupCode, { enabled: next });
        setSavingGroupCode(g.groupCode);
        try {
            const res = await compatApi.put(`/push/master-trigger/${orgCode}/group/${g.groupCode}/enabled`, { enabled: next });
            if (!res.ok) throw new Error();
            toast.success(`'${g.label}'을(를) ${next ? '켰습니다' : '껐습니다'}.`, toastConfig);
        } catch (error) {
            patchGroup(g.groupCode, { enabled: !next });
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : '알림 설정 변경에 실패했습니다.', toastConfig);
        } finally {
            setSavingGroupCode(null);
        }
    };

    // 하위 트리거 on/off는 토글 즉시 저장(낙관적 반영, 실패 시 롤백)
    const toggleEnabled = async (t) => {
        const next = !t.enabled;
        patchTrigger(t.triggerCode, { enabled: next });
        try {
            const res = await compatApi.put(`/push/master-trigger/${orgCode}/${t.triggerCode}/enabled`, { enabled: next });
            if (!res.ok) throw new Error();
            toast.success(`'${t.label}' 알림을 ${next ? '켰습니다' : '껐습니다'}.`, toastConfig);
        } catch (error) {
            patchTrigger(t.triggerCode, { enabled: !next });
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : '알림 설정 변경에 실패했습니다.', toastConfig);
        }
    };

    // 문구(+N값)는 카드별 저장 버튼으로 저장
    const saveDetail = async (t) => {
        if (t.usesLeadDays) {
            const n = Number(t.leadDays);
            if (!Number.isInteger(n) || n < 1) {
                toast.warn('N값은 1 이상의 정수여야 합니다.', toastConfig);
                return;
            }
        }
        setSavingCode(t.triggerCode);
        try {
            const res = await compatApi.put(`/push/master-trigger/${orgCode}/${t.triggerCode}/template`,
                { title: t.title, body: t.body });
            if (!res.ok) throw new Error();
            if (t.usesLeadDays) {
                const res2 = await compatApi.put(`/push/master-trigger/${orgCode}/${t.triggerCode}/lead-days`,
                    { leadDays: Number(t.leadDays) });
                if (!res2.ok) throw new Error();
            }
            toast.success('알림 설정이 저장되었습니다.', toastConfig);
        } catch (error) {
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : '알림 설정 저장에 실패했습니다.', toastConfig);
        } finally {
            setSavingCode(null);
        }
    };

    // 수신자는 전체 교체(선택된 이메일 목록)로 저장 — 서버가 기존과의 차분(추가/삭제)을 처리한다.
    const saveRecipients = async (triggerCode, selectedUsers) => {
        setSavingRecipients(true);
        try {
            const res = await compatApi.put(`/push/master-trigger/${orgCode}/${triggerCode}/recipients`,
                { emailList: selectedUsers.map(p => p.userEmail) });
            if (!res.ok) throw new Error();
            await fetchTriggers(); // 서버 조인 결과(이름 등) 반영을 위해 새로 조회
            toast.success('수신자가 설정되었습니다.', toastConfig);
            setRecipientModalFor(null);
        } catch (error) {
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : '수신자 설정에 실패했습니다.', toastConfig);
        } finally {
            setSavingRecipients(false);
        }
    };

    // 칩의 X 클릭 — 모달 없이 그 한 명만 빼고 전체 교체 저장(서버는 항상 전체 목록 기준 diff)
    const removeRecipient = (t, email) => {
        const remaining = (t.recipients || [])
            .filter(r => r.email !== email)
            .map(r => ({ userEmail: r.email, userName: r.userName }));
        saveRecipients(t.triggerCode, remaining);
    };

    const inputStyle = {
        width: '100%', height: 38, borderRadius: 9, border: '1.5px solid #ececf2',
        background: '#fafaff', padding: '0 12px', fontSize: 13.5, color: '#1f1d2e',
        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    };
    const labelStyle = { fontSize: 12, color: '#8b8a99', fontWeight: 600, marginBottom: 6, display: 'block' };
    const toggleStyle = (on) => ({
        width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
        background: on ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : '#d9d8e3',
        display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start',
        transition: 'all 160ms',
    });
    const knobStyle = { width: 20, height: 20, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.2)' };

    if (loading) {
        return <div style={{ padding: '28px 0', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>불러오는 중…</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {groups.map((g) => (
                <div key={g.groupCode} style={{
                    background: 'white', borderRadius: 18, border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    {/* 그룹 헤더 — 실제 게이트라 꺼지면 서버 배치가 하위 트리거 상태와 무관하게 발송하지 않는다 */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                        padding: '16px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                        borderBottom: g.enabled ? '1px solid #ececf2' : 'none',
                    }}>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>{g.label}</h3>
                        <button onClick={() => toggleGroup(g)} disabled={savingGroupCode === g.groupCode}
                            title={g.enabled ? '알림 끄기' : '알림 켜기'} style={toggleStyle(g.enabled)}>
                            <span style={knobStyle} />
                        </button>
                    </div>

                    {/* OFF면 접힘 — 켜야만 의미가 생기는 하위 설정을 굳이 보여주지 않는다 */}
                    {g.enabled && (
                        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12, background: '#fbfaff' }}>
                            {g.triggers.map((t) => (
                                <div key={t.triggerCode} style={{
                                    background: 'white', borderRadius: 14, border: '1px solid #f0eff5', overflow: 'hidden',
                                }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '12px 18px', borderBottom: t.enabled ? '1px solid #f4f3f9' : 'none',
                                    }}>
                                        <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: '#1f1d2e' }}>{t.label}</h4>
                                        <button onClick={() => toggleEnabled(t)} title={t.enabled ? '알림 끄기' : '알림 켜기'} style={toggleStyle(t.enabled)}>
                                            <span style={knobStyle} />
                                        </button>
                                    </div>

                                    {t.enabled && (
                                    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                                        {t.usesLeadDays && (
                                            <div>
                                                <label style={labelStyle}>며칠 전에 알릴까요?</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <input type="number" min="1" value={t.leadDays ?? ''}
                                                        onChange={(e) => patchTrigger(t.triggerCode, { leadDays: e.target.value })}
                                                        style={{ ...inputStyle, width: 90 }} />
                                                    <span style={{ fontSize: 13, color: '#5d5b6e' }}>일 전</span>
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                <label style={{ ...labelStyle, marginBottom: 0 }}>추가 수신자</label>
                                                <button onClick={() => setRecipientModalFor(t.triggerCode)} style={{
                                                    height: 26, padding: '0 10px', borderRadius: 7,
                                                    border: `1.5px solid ${hexToRgba(PURPLE, .35)}`, background: 'white',
                                                    color: PURPLE, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                }}>구성원 검색</button>
                                            </div>
                                            {t.recipients && t.recipients.length > 0 ? (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                    {t.recipients.map(r => (
                                                        <span key={r.email} style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                                            height: 26, padding: '0 4px 0 10px', borderRadius: 999,
                                                            background: hexToRgba(PURPLE, .08), color: '#3a3849', fontSize: 12,
                                                        }}>
                                                            {r.userName || r.email}
                                                            <button onClick={() => removeRecipient(t, r.email)} disabled={savingRecipients}
                                                                title="수신자에서 제외" style={{
                                                                    width: 18, height: 18, borderRadius: '50%', border: 'none',
                                                                    background: 'transparent', color: '#8b8a99', cursor: savingRecipients ? 'default' : 'pointer',
                                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                                                                }}>
                                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    padding: '10px 12px', borderRadius: 9, border: '1.5px dashed #ececf2',
                                                    background: '#fafaff', color: '#8b8a99', fontSize: 12,
                                                }}>
                                                    추가로 지정된 수신자가 없습니다. 조직 마스터에게는 기본으로 발송됩니다.
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label style={labelStyle}>발송 제목</label>
                                            <input type="text" value={t.title ?? ''}
                                                onChange={(e) => patchTrigger(t.triggerCode, { title: e.target.value })}
                                                style={inputStyle} />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>발송 문구</label>
                                            <textarea value={t.body ?? ''} rows={2}
                                                onChange={(e) => patchTrigger(t.triggerCode, { body: e.target.value })}
                                                style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }} />
                                            <p style={{ margin: '6px 0 0', fontSize: 11.5, color: '#aaa9b7' }}>치환자: {t.placeholders}</p>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <button onClick={() => saveDetail(t)} disabled={savingCode === t.triggerCode} style={{
                                                height: 34, padding: '0 18px', borderRadius: 9, border: 'none',
                                                background: savingCode === t.triggerCode ? '#c7c5d2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                                color: 'white', fontSize: 12.5, fontWeight: 600,
                                                cursor: savingCode === t.triggerCode ? 'default' : 'pointer', fontFamily: 'inherit',
                                            }}>{savingCode === t.triggerCode ? '저장 중…' : '저장'}</button>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {recipientModalFor && (() => {
                const t = findTrigger(recipientModalFor);
                if (!t) return null;
                return (
                    <MemberPickerModal
                        orgCode={orgCode}
                        title={`'${t.label}' 추가 수신자 선택`}
                        initialSelected={(t.recipients || []).map(r => ({ userEmail: r.email, userName: r.userName }))}
                        saving={savingRecipients}
                        onCancel={() => setRecipientModalFor(null)}
                        onConfirm={(selected) => saveRecipients(t.triggerCode, selected)}
                    />
                );
            })()}
        </div>
    );
}

// 조직 기타 설정 — 현재는 "휴가자 알람 기능 사용 여부" 하나.
// 값은 wit_organization.use_vacation_alarm 에 저장되며, 서버 당일 휴가 알람 스케줄러가 이 값을 보고 발송 여부를 판단한다.
function VacationAlarmSetting({ isMaster }) {
    const { user } = useTokenStore();
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const orgCode = user?.currentOrganizationCode;
        if (!orgCode) { setLoading(false); return; }
        setLoading(true);
        (async () => {
            try {
                const res = await compatApi.get(`/org/vacation-alarm/${orgCode}`);
                if (res.ok) setEnabled(!!res.data);
            } catch (e) {
                // 조회 실패는 조용히(기본 false). 권한 문제는 인터셉터가 처리.
            } finally {
                setLoading(false);
            }
        })();
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await compatApi.post('/org/update-vacation-alarm', {
                organizationCode: user.currentOrganizationCode,
                useVacationAlarm: enabled,
            });
            if (res.ok) {
                toast.success('기타 설정이 저장되었습니다.', toastConfig);
            } else {
                toast.error('기타 설정 저장에 실패했습니다.', toastConfig);
            }
        } catch (error) {
            const msg = error?.response?.data;
            toast.error(typeof msg === 'string' && msg ? msg : '기타 설정 저장 중 오류가 발생했습니다.', toastConfig);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            background: 'white', borderRadius: 18, border: '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
            overflow: 'hidden', maxWidth: 640,
        }}>
            <div style={{
                padding: '16px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                borderBottom: '1px solid #ececf2',
            }}>
                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>기타 설정</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#8b8a99' }}>조직 단위의 부가 기능을 켜고 끕니다.</p>
            </div>

            <div style={{ padding: '18px 22px' }}>
                {loading ? (
                    <div style={{ padding: '28px 0', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>불러오는 중…</div>
                ) : (
                    <>
                        <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            cursor: isMaster ? 'pointer' : 'default', userSelect: 'none',
                        }}>
                            <span style={{
                                width: 18, height: 18, marginTop: 1, borderRadius: 5,
                                border: `1.5px solid ${enabled ? PURPLE : '#d4d3df'}`,
                                background: enabled ? PURPLE : 'white',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, transition: 'all 140ms',
                            }}>
                                {enabled && <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                            </span>
                            <input
                                type="checkbox" checked={enabled} disabled={!isMaster}
                                onChange={e => setEnabled(e.target.checked)}
                                style={{ display: 'none' }}
                            />
                            <span>
                                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#1f1d2e' }}>휴가자 알람 기능 사용 여부</span>
                                <span style={{ display: 'block', marginTop: 3, fontSize: 12.5, color: '#8b8a99', lineHeight: 1.5 }}>
                                    켜면 휴가자가 있는 날 오전 9시에 조직원 전체에게 휴가 현황 푸시 알람을 발송합니다.
                                </span>
                            </span>
                        </label>

                        {isMaster && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                                <button onClick={handleSave} disabled={saving} style={{
                                    height: 36, padding: '0 20px', borderRadius: 9, border: 'none',
                                    background: saving ? '#c7c5d2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                    color: 'white', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
                                }}>{saving ? '저장 중…' : '저장'}</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const SETTINGS_SECTIONS = [
    { key: 'position', label: '직급 관리' },
    { key: 'department', label: '부서 관리' },
    // 알림 설정은 서버 인가(@orgAccessPolicy.canManageOrganization)와 맞춰 MASTER에게만 노출
    { key: 'notification', label: '알림 설정', masterOnly: true },
    { key: 'etc', label: '기타 설정' },
];

export default function OrganizationSettings() {
    const { user } = useTokenStore();
    const isMaster = user?.currentRole === 'MASTER';
    const [section, setSection] = useState('position');

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>
                <OrgSectionTabs active="org-settings" />

                {/* 조직 설정 내부 서브탭 — 앞으로 늘어날 MASTER 전용 설정 화면을 여기 추가한다 */}
                <div style={{
                    display: 'inline-flex', gap: 4, background: 'white', padding: 4, borderRadius: 12,
                    border: '1px solid #ececf2', boxShadow: '0 1px 2px rgba(60,50,120,.04)', marginBottom: 16,
                }}>
                    {SETTINGS_SECTIONS.filter(s => !s.masterOnly || isMaster).map((s) => {
                        const isOn = s.key === section;
                        return (
                            <button key={s.key} onClick={() => setSection(s.key)} style={{
                                height: 32, padding: '0 14px', borderRadius: 8, border: 'none',
                                background: isOn ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : 'transparent',
                                color: isOn ? 'white' : '#5d5b6e',
                                fontSize: 12.5, fontWeight: isOn ? 600 : 500,
                                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                boxShadow: isOn ? `0 6px 14px -8px ${hexToRgba(PURPLE, .55)}` : 'none',
                                transition: 'all 160ms',
                            }}>{s.label}</button>
                        );
                    })}
                </div>

                {section === 'position' && (
                    <ReorderableListEditor
                        heading="직급 체계"
                        hint="위에 있을수록 상위 직급입니다. 왼쪽 손잡이를 드래그해 순서를 바꾸세요. 회원 직급 선택·정렬에 이 순서가 사용됩니다."
                        itemLabel="직급" itemNamePlural="직급"
                        fetchPath="/org/positions" savePath="/org/positions" saveField="positions" itemField="positionName"
                        isMaster={isMaster}
                    />
                )}
                {section === 'department' && (
                    <ReorderableListEditor
                        heading="부서 목록"
                        hint="왼쪽 손잡이를 드래그해 순서를 바꾸세요. 회원 부서 선택에 이 목록이 사용됩니다."
                        itemLabel="부서" itemNamePlural="부서"
                        fetchPath="/org/departments" savePath="/org/departments" saveField="departments" itemField="departmentName"
                        isMaster={isMaster}
                    />
                )}
                {section === 'etc' && (
                    <VacationAlarmSetting isMaster={isMaster} />
                )}
                {section === 'notification' && isMaster && <MasterTriggerSettings />}
            </main>
        </div>
    );
}
